import { Injectable, OnDestroy } from '@angular/core';
import { NEVER, Observable, of, zip } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OrgUserProfile } from 'interfaces/profile.interface';
import { CountryFieldOption } from 'services/locations/locations.constant';
import { skipModalClose, SmartForm, SmartModalService } from '@certemy/ui-components';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { EditProfileModalComponent } from 'components/profile-card/modals/edit-profile/edit-profile.modal.component';
import { DEFAULT_MODAL_OPTIONS } from 'constants/constants';
import { LocationsService } from 'services/locations/locations.service';
import { ROLE_CODES } from 'constants/role.constants';
import { ProfileService } from 'services/profile/profile.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import _get from 'lodash-es/get';
import { UserProfile } from 'services/user/user.interface';
import { Actions } from 'features/profiles-state/+state/profiles-state.actions';
import { Store } from '@ngrx/store';
import { IPermissions } from 'services/policy-permission/policy-permissions.service';
import { ClientServicesService } from 'services/client-services/client-services.service';
import { OrganizationRolesService } from 'features/organization-settings/organization-roles/organization-roles.service';
import { PublicGlobalService } from 'services/public-global/public-global.service';
import { Country, DEFAULT_MOBILE_COUNTRY_CODE } from 'interfaces/public-global.interface';

@UntilDestroy()
@Injectable()
export class EditProfileModalService implements OnDestroy {
  constructor(
    private locationsService: LocationsService,
    private profileService: ProfileService,
    private modalService: NgbModal,
    private smartModalService: SmartModalService,
    private store: Store<any>,
    private clientServicesService: ClientServicesService,
    private organizationRolesService: OrganizationRolesService,
    private publicGlobalService: PublicGlobalService,
  ) {}

  ngOnDestroy() {}

  showModalEditProfile(
    profile,
    currentProfile: UserProfile,
    permissions: IPermissions,
    updateProfileCallback,
    isGlobalProfile: boolean,
    highlightId = '',
  ): void {
    const locations = profile.locations;
    const additionalLocations = profile.additionalLocations;
    const locationCountiesIds = _get(locations, 'countries.ids');
    const locationRegionsIds = _get(locations, 'regions.ids');
    const additionalCountiesIds = _get(additionalLocations, 'countries.ids');
    const additionalRegionsIds = _get(additionalLocations, 'regions.ids');
    const countries$: Observable<CountryFieldOption[]> = permissions.locationsFeatureFlag
      ? isGlobalProfile
        ? this.clientServicesService.getCountries(profile.organization_id, {
            withOffices: true,
          })
        : this.locationsService.getLocationOrganizationCountries({
            withOffices: true,
          })
      : of([]);
    const regions$: Observable<SmartForm.FieldOption[]> =
      permissions.locationsFeatureFlag && locationCountiesIds
        ? isGlobalProfile
          ? this.clientServicesService.getRegions(profile.organization_id, locationCountiesIds, {
              withOffices: true,
            })
          : this.locationsService.getLocationOrganizationRegions(locationCountiesIds, {
              withOffices: true,
            })
        : of([]);
    const additionalRegions$: Observable<SmartForm.FieldOption[]> =
      permissions.locationsFeatureFlag && additionalCountiesIds
        ? isGlobalProfile
          ? this.clientServicesService.getRegions(profile.organization_id, additionalCountiesIds, {
              withOffices: true,
            })
          : this.locationsService.getLocationOrganizationRegions(additionalCountiesIds, {
              withOffices: true,
            })
        : of([]);
    const offices$: Observable<SmartForm.FieldOption[]> =
      permissions.locationsFeatureFlag && locationRegionsIds
        ? isGlobalProfile
          ? this.clientServicesService.getOffices(profile.organization_id, locationRegionsIds)
          : this.locationsService.getLocationOrganizationOffices(locationRegionsIds)
        : of([]);
    const additionalOffices$: Observable<SmartForm.FieldOption[]> =
      permissions.locationsFeatureFlag && additionalRegionsIds
        ? isGlobalProfile
          ? this.clientServicesService.getOffices(profile.organization_id, additionalRegionsIds)
          : this.locationsService.getLocationOrganizationOffices(additionalRegionsIds)
        : of([]);
    const titles$: Observable<SmartForm.FieldOption[]> =
      permissions.titlesFeatureFlag && profile.role_id === ROLE_CODES.ORGANIZATION_ADMIN
        ? isGlobalProfile
          ? this.clientServicesService.getTitles(profile.organization_id)
          : this.profileService.getOrgAdminTitleOptions()
        : of([]);
    const organizationRoles$: Observable<SmartForm.FieldOption[]> =
      permissions.organizationRolesFeatureFlag && profile.role_id === ROLE_CODES.PROFESSIONAL
        ? isGlobalProfile
          ? this.clientServicesService.getOrganizationRoles(profile.organization_id)
          : this.organizationRolesService
              .getOrganizationRoles()
              .pipe(
                map((response) => response.map(({ organizationRoleId, name }) => ({ id: organizationRoleId, name }))),
              )
        : of([]);
    const phoneCodes$: Observable<Country[]> = this.publicGlobalService.getCountries(true);

    zip(
      countries$,
      regions$,
      offices$,
      additionalRegions$,
      additionalOffices$,
      titles$,
      organizationRoles$,
      phoneCodes$,
    )
      .pipe(
        take(1),
        switchMap(
          ([
            countries,
            regions,
            offices,
            additionalRegions,
            additionalOffices,
            titles,
            organizationRoles,
            phoneCodes,
          ]) => {
            return Observable.create((observer) => {
              const modalRef = this.modalService.open(EditProfileModalComponent, {
                ...DEFAULT_MODAL_OPTIONS,
                size: 'lg',
              });
              const phoneCodeCountries = phoneCodes.map((item: Country) => ({ id: item.countryId, name: item.name }));
              const defaultPhoneCountry = phoneCodes.find(({ code }) => code === DEFAULT_MOBILE_COUNTRY_CODE);
              Object.assign(modalRef.componentInstance, {
                profile,
                currentProfile,
                countries,
                regions,
                offices,
                additionalRegions,
                additionalOffices,
                titles,
                organizationRoles,
                phoneCodeCountries,
                defaultPhoneCountryId: _get(defaultPhoneCountry, 'countryId', null),
                isGlobalProfile,
                highlightId,
              });
              modalRef.componentInstance.updateProfile.subscribe((updatedProfile: OrgUserProfile) => {
                if (profile.profile_id === currentProfile.profile_id) {
                  this.store.dispatch(new Actions.GetProfiles());
                }
                updateProfileCallback(updatedProfile);
              });
              modalRef.result.then(
                (payload) => observer.next({ type: this.smartModalService.CloseType.SUCCESS, payload }),
                (payload) => observer.next({ type: this.smartModalService.CloseType.REJECT, payload }),
              );
              return () => modalRef.close();
            });
          },
        ),
        untilDestroyed(this),
        skipModalClose(),
        catchError(() => NEVER),
      )
      .subscribe(({ payload }: { payload: OrgUserProfile }) => {
        if (profile.profile_id === currentProfile.profile_id) {
          this.store.dispatch(new Actions.GetProfiles());
        }
        updateProfileCallback(payload);
      });
  }
}
